import { signInWithCustomToken } from 'firebase/auth';
import { clearFirestoreCache } from './firestore.js';

/**
 * Consume a cross-app auth token from the URL (`?token=<customToken>`) at
 * module load, signing the user into Firebase Auth. The token is stripped
 * from the URL to keep it out of history/logs.
 *
 * URL cleanup defaults to `window.history.replaceState`, but SvelteKit
 * callers should provide `scrubUrl` — SvelteKit's initial `navigate()`
 * re-syncs `history` at the end of the first render with the URL it
 * captured at nav-start (which still contains `?token=`), overwriting a
 * plain `history.replaceState`. The callback is where the app reconciles
 * with its framework router (e.g. `replaceState` from `$app/navigation`).
 *
 * Returns a Promise<UserCredential | undefined> that the caller's
 * `onAuthStateChanged` handler should await before processing the fired
 * auth state (so the handler sees the custom-token-authenticated user).
 * Returns `null` when no token is present.
 *
 * @param {import('firebase/auth').Auth} auth
 * @param {object} [opts]
 * @param {(err: Error) => void} [opts.onError]
 * @param {(cleanUrl: URL) => void} [opts.scrubUrl] - Framework-aware URL cleanup.
 *        Receives the URL with `token` already stripped. Must call
 *        `history.replaceState`/framework equivalent itself.
 * @returns {Promise|null}
 */
export function handleAuthTokenHandoff(auth, { onError, scrubUrl } = {}) {
    if (typeof window === 'undefined') return null;

    const url = new URL(window.location.href);
    const incomingToken = url.searchParams.get('token');
    if (!incomingToken) return null;

    url.searchParams.delete('token');
    if (scrubUrl) {
        scrubUrl(url);
    } else {
        window.history.replaceState({}, '', url.toString());
    }

    console.debug('[auth-handoff] processing cross-app custom token');
    return signInWithCustomToken(auth, incomingToken).catch((err) => {
        console.error('[auth-handoff] sign-in failed', err);
        if (onError) onError(err);
        throw err;
    });
}

/**
 * Consume an impersonation nonce from the URL (`?impersonate=<nonce>`) at
 * module load. Cross-origin only — the admin app stores a token server-side
 * keyed by the nonce, and the receiving app redeems it via
 * `redeemImpersonationExchange`. Education's local same-origin impersonation
 * (localStorage-keyed) is handled in education's Auth.js directly.
 *
 * Returns a Promise the caller's `onAuthStateChanged` handler should await
 * before processing the fired auth state, or `null` if no nonce is present.
 *
 * @param {import('firebase/auth').Auth} auth
 * @param {(name: string) => (...args) => Promise} callable - from createFunctions
 * @param {object} [opts]
 * @param {(err: Error) => void} [opts.onError]
 * @param {(cleanUrl: URL) => void} [opts.scrubUrl]
 * @param {() => void} [opts.onActive] - fires once we've decided to redeem
 *        (use this to flag the tab as impersonating, e.g. ghostToken.set('active'))
 * @returns {Promise|null}
 */
export function handleImpersonationHandoff(auth, callable, { onError, scrubUrl, onActive } = {}) {
    if (typeof window === 'undefined') return null;

    const url = new URL(window.location.href);
    const nonce = url.searchParams.get('impersonate');
    if (!nonce) return null;

    url.searchParams.delete('impersonate');
    if (scrubUrl) {
        scrubUrl(url);
    } else {
        window.history.replaceState({}, '', url.toString());
    }

    if (onActive) {
        try { onActive(); } catch (err) { console.error('[impersonation] onActive threw', err); }
    }

    console.debug('[impersonation] redeeming nonce via exchange');
    const redeemFn = callable('redeemImpersonationExchange');
    return (async () => {
        const response = await redeemFn({ nonce });
        const token = response?.data?.token;
        if (!token) throw new Error('No token returned from exchange');
        await signInWithCustomToken(auth, token);
    })().catch((err) => {
        console.error('[impersonation] sign-in failed', err);
        if (onError) onError(err);
        throw err;
    });
}

/**
 * Pure check against an already-fetched claims map. Use this when you already
 * have a fresh `getIdTokenResult()` result (e.g. the caller just refreshed to
 * read MFA claims) and don't want a duplicate network round-trip.
 *
 * @param {object | null | undefined} claims - result.claims from getIdTokenResult
 * @param {'education' | 'tap' | 'admin'} expectedApp
 * @returns {{ ok: boolean, claim: string | null }}
 */
export function checkSessionAppClaim(claims, expectedApp) {
    const claim = claims?.targetApp ?? null;
    return { ok: claim === expectedApp, claim };
}

/**
 * Force an ID-token refresh for a signed-in user and report whether the
 * refreshed token carries the expected `targetApp` developer claim. The claim
 * is set on custom tokens minted by generateAppToken and impersonation, and
 * flows into the ID token. Callers are expected to sign the user out and
 * redirect to the Auth app when `ok` is false.
 *
 * Prefer `checkSessionAppClaim(claims, expectedApp)` when the caller already
 * holds a fresh `result.claims` and only needs the verdict.
 *
 * @param {import('firebase/auth').User} user
 * @param {'education' | 'tap' | 'admin'} expectedApp
 * @returns {Promise<{ ok: boolean, claim: string | null }>}
 */
export async function verifySessionAppClaim(user, expectedApp) {
    if (!user) return { ok: false, claim: null };
    try {
        const result = await user.getIdTokenResult(true);
        return checkSessionAppClaim(result.claims, expectedApp);
    } catch (err) {
        console.error('[auth-handoff] failed to verify targetApp claim', err);
        return { ok: false, claim: null };
    }
}

/**
 * Fire the `revokeUserTokens` Cloud Function but cap how long sign-out waits
 * on it. Revocation is a security backstop it invalidates the session on
 * other origins, and refresh tokens expire on their own within ~1h regardless
 *
 * The call still runs to completion in the background; we just stop awaiting
 * it after `timeoutMs`. Returns once the revoke resolves OR the timeout
 * elapses, whichever comes first. Never rejects.
 *
 * @param {(name: string) => (...args) => Promise} callable - from createFunctions
 * @param {number} [timeoutMs]
 * @returns {Promise<void>}
 */
export function revokeTokensWithTimeout(callable, timeoutMs = 1500) {
    const call = (async () => {
        try {
            const revokeUserTokens = callable('revokeUserTokens');
            await revokeUserTokens();
        } catch (error) {
            console.error('[auth-handoff] token revocation failed:', error);
            // Continue with local sign-out even if revocation fails.
        }
    })();

    const timeout = new Promise((resolve) => {
        setTimeout(resolve, timeoutMs);
    });

    return Promise.race([call, timeout]);
}

/**
 * Create a sign-out handler that revokes refresh tokens server-side (so all
 * apps are signed out within ~1h), then signs out locally and redirects to
 * the Auth app.
 *
 * @param {object} opts
 * @param {import('firebase/auth').Auth} opts.auth
 * @param {(name: string) => (...args) => Promise} opts.callable - from createFunctions
 * @param {string} opts.authUrl - VITE_AUTH_URL for the environment
 * @param {() => void} [opts.onBeforeRedirect] - hook for clearing stores, analytics, etc.
 * @param {import('firebase/firestore').Firestore[]} [opts.databases] - Firestore
 *        instances whose persisted IndexedDB cache should be wiped on sign-out.
 *        Required on shared devices so the next user can't read cached docs.
 * @returns {() => Promise<void>}
 */
export function createCrossAppSignOut({ auth, callable, authUrl, onBeforeRedirect, databases = [] }) {
    return async function signOut() {
        await revokeTokensWithTimeout(callable);

        if (onBeforeRedirect) {
            try {
                onBeforeRedirect();
            } catch (err) {
                console.error('[auth-handoff] onBeforeRedirect threw:', err);
            }
        }

        await auth.signOut();

        // Wipe persisted Firestore caches
        if (databases.length) {
            await Promise.all(databases.map((db) => clearFirestoreCache(db)));
        }

        if (typeof window !== 'undefined' && authUrl) {
            const target = new URL(authUrl);
            target.searchParams.set('signedOut', '1');
            window.location.href = target.toString();
        }
    };
}
